import React, { createContext, useState, useEffect } from "react";
import App from "../app/App";
import { checkUserSession } from "../api/auth";
import { setAuthToken, parseApiResponse } from "../api/index";
import { User } from "../types/user";

export const GlobalContext = createContext<any>({});

export const GlobalContainer = (props: any) => {
  const [isMobile, setIsMobile] = useState(true);
  const [isSmallScreen, setIsSmallScreen] = useState(true);

  const [analytics, setAnalytics] = useState(
    localStorage.getItem("cookieAnalytics")
      ? +localStorage.getItem("cookieAnalytics")!
      : 0,
  );
  const [preference, setPreference] = useState(
    localStorage.getItem("cookiePreference")
      ? +localStorage.getItem("cookiePreference")!
      : 0,
  );
  const [privacy, setPrivacy] = useState(
    localStorage.getItem("cookiePrivacy")
      ? +localStorage.getItem("cookiePrivacy")!
      : 0,
  );

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  // True until the initial /auth/me check finishes; lets UI avoid the
  // flash-of-logged-out state on hard refresh while a valid token is verified.
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [customAlert, setCustomAlert] = useState<React.ReactNode>();

  function setAnalyticsFunc(n: any) {
    setAnalytics(n);
    props.setAnalytics(n);
  }

  useEffect(() => {
    const verifyUserSession = async () => {
      try {
        const response = await checkUserSession();

        // No token at all → not logged in, no need to clear anything.
        if (!response) {
          setCurrentUser(null);
          return;
        }

        if (response.ok) {
          const userData = await parseApiResponse<{ user: User }>(response);
          if (userData?.user) {
            setCurrentUser(userData.user);
          } else {
            setCurrentUser(null);
            setAuthToken(null);
          }
          return;
        }

        // Only clear the token if the server explicitly rejected it. Network
        // failures (status 0) or 5xxs are transient — keep the token and the
        // user can retry; we just leave them as "not logged in" for now.
        if (response.status === 401) {
          setCurrentUser(null);
          setAuthToken(null);
        } else {
          setCurrentUser(null);
        }
      } catch (error) {
        console.error("Session check failed:", error);
        // Do NOT clear the token on network errors — only on explicit 401.
      } finally {
        setIsAuthLoading(false);
      }
    };

    verifyUserSession();

    // Global 401 handler: any call going through fetchWithAuth that hits 401
    // dispatches this event. We respond by logging out locally.
    const handleUnauthorized = () => {
      setCurrentUser(null);
      setAuthToken(null);
    };
    window.addEventListener("auth:unauthorized", handleUnauthorized);

    // Cross-tab sync: log in/out in one tab → mirror in the other tab.
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "eventshouse_auth_token") {
        if (!e.newValue) {
          // Token was cleared in another tab.
          setCurrentUser(null);
        } else if (e.newValue !== e.oldValue) {
          // Different token in another tab — re-fetch the user.
          void verifyUserSession();
        }
      }
    };
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("auth:unauthorized", handleUnauthorized);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  useEffect(() => {
    setIsMobile(window.innerWidth < 430);
    setIsSmallScreen(window.innerWidth < 640);

    function handleResize() {
      setIsMobile(window.innerWidth < 430);
      setIsSmallScreen(window.innerWidth < 640);
    }

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = customAlert ? "hidden" : "visible";
    const main = document.querySelector("main");
    if (main) {
      main.style.opacity = customAlert ? "0.3" : "1";
    }
  }, [customAlert]);

  return (
    <GlobalContext.Provider
      value={{
        isMobile,
        setIsMobile,
        isSmallScreen,
        setIsSmallScreen,
        analytics,
        setAnalyticsFunc,
        preference,
        setPreference,
        privacy,
        setPrivacy,
        currentUser,
        setCurrentUser,
        isAuthLoading,
        customAlert,
        setCustomAlert,
      }}
    >
      <App />
    </GlobalContext.Provider>
  );
};
